import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Toast notification types
 */
export type ToastType = 'success' | 'error' | 'warning' | 'info';

/**
 * Toast notification model
 */
export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message: string;
  duration?: number; // milliseconds, 0 = no auto-dismiss
  dismissible?: boolean;
  icon?: string; // Bootstrap icon class
  action?: {
    label: string;
    callback: () => void;
  };
  createdAt: Date;
}

/**
 * Options for creating a toast
 */
export interface ToastOptions {
  type?: ToastType;
  title?: string;
  duration?: number;
  dismissible?: boolean;
  icon?: string;
  action?: {
    label: string;
    callback: () => void;
  };
}

/**
 * Default durations by toast type (in milliseconds)
 */
const DEFAULT_DURATIONS: Record<ToastType, number> = {
  success: 4000,
  error: 8000,
  warning: 6000,
  info: 5000
};

/**
 * Default icons by toast type (Bootstrap Icons)
 */
const DEFAULT_ICONS: Record<ToastType, string> = {
  success: 'bi-check-circle-fill',
  error: 'bi-exclamation-triangle-fill',
  warning: 'bi-exclamation-circle-fill',
  info: 'bi-info-circle-fill'
};

/**
 * Toast Notification Service
 *
 * Provides a simple, Bootstrap-based toast notification system.
 * Toasts appear in the top-right corner and auto-dismiss by default.
 *
 * Usage:
 * ```typescript
 * // Simple usage
 * this.toastService.success('Operation completed successfully');
 * this.toastService.error('Something went wrong');
 *
 * // With options
 * this.toastService.show('Your ticket has been updated', {
 *   type: 'info',
 *   title: 'Ticket Update',
 *   duration: 6000,
 *   action: {
 *     label: 'View',
 *     callback: () => this.router.navigate(['/tickets', ticketId])
 *   }
 * });
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private toastsSubject = new BehaviorSubject<Toast[]>([]);
  public toasts$: Observable<Toast[]> = this.toastsSubject.asObservable();

  private idCounter = 0;

  /**
   * Show a toast notification
   */
  show(message: string, options: ToastOptions = {}): string {
    const id = this.generateId();
    const type = options.type || 'info';

    const toast: Toast = {
      id,
      type,
      title: options.title || this.getDefaultTitle(type),
      message,
      duration: options.duration ?? DEFAULT_DURATIONS[type],
      dismissible: options.dismissible ?? true,
      icon: options.icon || DEFAULT_ICONS[type],
      action: options.action,
      createdAt: new Date()
    };

    // Add toast to the list
    const currentToasts = this.toastsSubject.value;
    this.toastsSubject.next([...currentToasts, toast]);

    // Auto-dismiss if duration is set
    if (toast.duration && toast.duration > 0) {
      setTimeout(() => this.dismiss(id), toast.duration);
    }

    return id;
  }

  /**
   * Show a success toast
   */
  success(message: string, options: Omit<ToastOptions, 'type'> = {}): string {
    return this.show(message, { ...options, type: 'success' });
  }

  /**
   * Show an error toast
   */
  error(message: string, options: Omit<ToastOptions, 'type'> = {}): string {
    return this.show(message, { ...options, type: 'error' });
  }

  /**
   * Show a warning toast
   */
  warning(message: string, options: Omit<ToastOptions, 'type'> = {}): string {
    return this.show(message, { ...options, type: 'warning' });
  }

  /**
   * Show an info toast
   */
  info(message: string, options: Omit<ToastOptions, 'type'> = {}): string {
    return this.show(message, { ...options, type: 'info' });
  }

  /**
   * Dismiss a specific toast by ID
   */
  dismiss(id: string): void {
    const currentToasts = this.toastsSubject.value;
    this.toastsSubject.next(currentToasts.filter(t => t.id !== id));
  }

  /**
   * Dismiss all toasts
   */
  dismissAll(): void {
    this.toastsSubject.next([]);
  }

  /**
   * Get current toasts (snapshot)
   */
  getToasts(): Toast[] {
    return this.toastsSubject.value;
  }

  /**
   * Generate a unique toast ID
   */
  private generateId(): string {
    return `toast-${++this.idCounter}-${Date.now()}`;
  }

  /**
   * Get default title based on toast type
   */
  private getDefaultTitle(type: ToastType): string {
    switch (type) {
      case 'success': return 'Success';
      case 'error': return 'Error';
      case 'warning': return 'Warning';
      case 'info': return 'Notice';
    }
  }
}
